import Checkbox from '@/app/components/common/Checkbox'
import Text from '@/app/components/common/Text'
import { FILTER_TYPES, POPULAR_FILTERS } from '@/app/constants'
import { Filters, FiltersCountMap } from '@/app/types'
import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import { FC, useRef } from 'react'

interface PopularFiltersProps {
	localValue: Filters
	isFilterSticky: boolean
	availableFilters: FiltersCountMap
	handlePopularFilterChange: (filter: {
		id: number
		type: string
		name: string
	}) => void
}

const PopularFilters: FC<PopularFiltersProps> = ({
	localValue,
	availableFilters,
	handlePopularFilterChange,
	isFilterSticky,
}) => {
	const popularFiltersRef = useRef<HTMLDivElement>(null)
	const t = useTranslations('Keywords')
	const tFilters = useTranslations('Filters')

	const filterTypeMap: { [key: string]: keyof Filters } = {
		stars: FILTER_TYPES.STARS,
		stayOptions: FILTER_TYPES.STAY_OPTIONS,
		rating: FILTER_TYPES.RATING,
		facilities: FILTER_TYPES.FACILITIES,
	}

	// Simple direct mapping from frontend filter types to backend categories
	const filterToBackendMap: Record<string, keyof FiltersCountMap> = {
		[FILTER_TYPES.STARS]: 'starRatings',
		[FILTER_TYPES.STAY_OPTIONS]: 'propertyTypes',
		[FILTER_TYPES.RATING]: 'guestRatings',
		[FILTER_TYPES.FACILITIES]: 'facilities',
		hotelStyles: 'hotelThemes',
	}

	const isFilterChecked = (filter: { id: number; type: string }) => {
		const filterKey = filterTypeMap[filter.type]
		return filterKey && Array.isArray(localValue[filterKey])
			? localValue[filterKey].includes(Number(filter.id))
			: false
	}

	// Get the count for a specific filter from the backend data
	const getFilterCount = (filter: { id: number; type: string }): number => {
		if (!availableFilters) return 0

		// Get the corresponding backend category directly from the map
		const backendCategory = filterToBackendMap[filter.type]
		if (!backendCategory) return 0

		// Access the count using the backend category
		const categoryData = availableFilters[backendCategory]
		if (!categoryData) return 0

		// Return the count for this filter ID
		return categoryData[filter.id] || 0
	}

	const formatFilterLabel = (filter: {
		id: number
		type: string
		name: string
	}): string => {
		const count = getFilterCount(filter)
		const baseLabel = tFilters(filter.name)

		return `${baseLabel} (${count})`
	}

	return (
		<div
			className={classNames('hotel-list-filters__section', {
				'fixed top-[116px] w-[266px]': isFilterSticky,
			})}
			ref={popularFiltersRef}
		>
			<Text size="lg" font={'semibold'} className="hotel-list-filters__title">
				{t('popularFilters')}
			</Text>
			<ul className="hotel-list-filters__popular">
				{POPULAR_FILTERS.map((filter) => (
					<div
						key={filter.id}
						className="hotel-list-filters__popular-item hotel-list-filters__item"
					>
						<Checkbox
							variant="main-dark"
							checked={isFilterChecked(filter)}
							testId={`popular-filter-${filter.id}`}
							onChange={() => handlePopularFilterChange(filter)}
							labelRight={formatFilterLabel(filter)}
							bold={false}
						/>
					</div>
				))}
			</ul>
		</div>
	)
}

export default PopularFilters
